import React, {
  useState,
  memo,
  useEffect,
  useMemo,
  useRef,
  useContext,
  useCallback,
} from "react";
import {useTranslation} from "react-i18next";
import {Formik, useFormikContext, Form} from "formik";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import * as yup from "yup";
//local
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {RichEditor} from "components/RichEditor";
import {TitleIcon} from "components/TitleIcon";
import MiniSelect from "components/selects/MiniSelect";
import {
  IconTrashCan,
  IconChevronLeft,
  IconPlus,
  IconFileAdd,
  IconMailLetter,
} from "components/icons";
import {OurProfilesModal} from "pages/SignContracts/components/OurProfilesModal";
import SignContractInvoiceProfileData from "./atoms/SignContractInvoiceProfileData";
import SignContractFileDataView from "./atoms/SignContractFileDataView";
import {SignContractProfileField} from "./atoms/SignContractProfileField";
import {ConfirmModal} from "components/ConfirmModal";
import {color} from "constants/color.consts";
import {trimObjectValues} from "helpers/helper";
import {dateFormat} from "helpers/datetime";
import {getCountries} from "../api/organization";
import {emailNullable} from "yup/defaultValudations";
import {SignContractContext} from "../SignContractContext";
import {
  generateSignContractFile,
  getRecentlyInvoiceProfiles,
  getOrganizationMembersForSignContractMember,
  getOrganizationForSignContractMember,
} from "../api/contracts";
import {CompanyAutocompleteField} from "./atoms/CompanyAutocompleteField";
import {CounterpartyContactAutocompleteField} from "./atoms/CounterpartyContactAutocompleteField";
import {itemFileDataLanguageKey, fileDataFieldProps} from "../constants";
import {templatesType} from "components/RichEditor";

const schema = yup.object().shape({
  initiatingContactEmail: emailNullable(),
  deliveryContactEmail: emailNullable(),
  counterpartyContactEmail: emailNullable(),
});

export const FormikValuesListener = ({onChangeValues}) => {
  const {values, dirty} = useFormikContext();

  const isFirstRender = useRef(true);
  const lastUserValues = useRef(values);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastUserValues.current = values;
      return;
    }

    if (!dirty) return;
    if (isEqual(lastUserValues.current, values)) return;

    lastUserValues.current = values;
    onChangeValues(values);
  }, [values, dirty, onChangeValues]);

  return null;
};

const SignContractFileData = memo(({contract, isOwner, onChange, onBack}) => {
  const {t} = useTranslation();
  const {status, language} = useContext(SignContractContext);

  const [isCompanyProfilesModal, setCompanyProfilesModal] = useState(false);
  const [countries, setCountries] = useState([]);
  const [recentlyProfiles, setRecentlyProfiles] = useState([]);
  const [loadingCreatePdf, setLoadingCreatePdf] = useState(false);
  const [counterpartyOrgMembers, setCounterpartyOrgMembers] = useState([]);

  const editMode = useMemo(() => !!status.isDraft, [status]);

  const clickChangeProfile = (profile) =>
    onChange("HsContractFileInvoiceProfileId", profile.id, "refresh");

  const onDeleteContractData = () => {
    const data = {
      HsContractFileDatum: null,
      HsContractFileInvoiceProfileId: null,
      HsContractFileInvoiceProfile: null,
    };
    onChange(undefined, data, "update").then(onBack);
  };

  const handleDeleteContractData = () =>
    ConfirmModal({
      title: t(`${itemFileDataLanguageKey}DELETE_MODAL_TITLE`),
      text: t(`${itemFileDataLanguageKey}DELETE_MODAL_TEXT`),
      type: "warning",
      noQuestionSymbol: true,
      onOk: onDeleteContractData,
    });

  const onChangeField = useMemo(
    () => debounce((values) => onChange("HsContractFileDatum", values), 1000),
    [onChange]
  );

  const handleCounterpartyOrganizationPicked = useCallback((organizationId) => {
    if (!organizationId) return setCounterpartyOrgMembers([]);
    getOrganizationMembersForSignContractMember(organizationId)
      .then(setCounterpartyOrgMembers)
      .catch(() => setCounterpartyOrgMembers([]));
  }, []);

  const handleSearchOrganizations = (value, extraData) =>
    getOrganizationForSignContractMember(value, undefined, extraData);

  const FileDataHeader = memo(() => {
    const {values} = useFormikContext();

    const formSignContractFile = () => {
      setLoadingCreatePdf(true);
      const dateNow = dateFormat(new Date());
      const divider = `・${t("SIGN_CONTRACT_PDF_FILENAME_START")}・`;
      const fileName = `${contract.number}${divider}${dateNow}`;
      generateSignContractFile(contract.id, {fileName, language})
        .then(onBack)
        .finally(() => setLoadingCreatePdf(false));
    };

    return (
      <div className="df-row-center mb-6">
        <ButtonTransparent
          noBorder
          name={`${itemFileDataLanguageKey}BACK_BUTTON`}
          bold
          small
          uppercase
          type="button"
          onClick={onBack}
          icon={<IconChevronLeft color={color.blue} />}
          addClass="fz-14 black pl-0"
        />
        {editMode && !!countries.length && (
          <ButtonTransparent
            name={`${itemFileDataLanguageKey}DELETE_DATA_BUTTON`}
            addClass="mla pl-1 pr-1"
            small
            type="button"
            color="red"
            icon={<IconTrashCan />}
            disabled={loadingCreatePdf}
            onClick={handleDeleteContractData}
          />
        )}
        {editMode && !!countries.length && (
          <ButtonTransparent
            name={`${itemFileDataLanguageKey}CREATE_PDF_BUTTON`}
            addClass="ml-2 pl-1 pr-1"
            small
            type="button"
            icon={<IconFileAdd />}
            disabled={!values.companyName}
            loading={loadingCreatePdf}
            onClick={formSignContractFile}
            nonTransparent
          />
        )}
      </div>
    );
  });

  const InvoiceProfileSection = memo(() => (
    <React.Fragment>
      <h4 className="__bold mb-2">
        {t(`${itemFileDataLanguageKey}INVOICE_PROFILE_DATA_TITLE`)}
      </h4>
      {contract.HsContractFileInvoiceProfile ? (
        <SignContractInvoiceProfileData
          profile={trimObjectValues(contract.HsContractFileInvoiceProfile)}
          countries={countries}
          onRemove={
            editMode &&
            (() => onChange("HsContractFileInvoiceProfileId", null, "refresh"))
          }
          addClass="mb-4"
        />
      ) : (
        <div className="df-row-center mb-4">
          <ButtonTransparent
            name="SIGN_CONTRACT_INVOICING_PROFILES_BUTTON"
            onClick={() => setCompanyProfilesModal(true)}
            icon={<IconPlus />}
            nonTransparent
            tiny
            uppercase
            rounded
            addClass="pl-1"
          />
          {!!recentlyProfiles.length &&
            recentlyProfiles.map((profile) => (
              <ButtonTransparent
                key={profile.id}
                name={profile.name}
                onClick={() => clickChangeProfile(profile)}
                icon={<IconPlus />}
                tiny
                uppercase
                rounded
                addClass="pl-1 ml-2"
              />
            ))}
        </div>
      )}
    </React.Fragment>
  ));

  const ProfileField = memo((props) => (
    <SignContractProfileField {...props} addClass={props.addClass ?? "mb-1"} />
  ));

  useEffect(() => {
    getCountries(language).then(setCountries);
  }, [language]);

  useEffect(() => {
    getRecentlyInvoiceProfiles().then(setRecentlyProfiles);
    setCounterpartyOrgMembers([]);
  }, []);

  useEffect(() => {
    return () => {
      onChangeField.cancel();
    };
  }, [onChangeField]);

  return (
    <Formik
      initialValues={contract.HsContractFileDatum ?? {}}
      validationSchema={schema}
      enableReinitialize
    >
      {({values, setFieldValue}) => {
        return (
          <Form
            className={`signing_contract__data__file-data${editMode ? " __edit" : ""}`}
          >
            <FormikValuesListener onChangeValues={onChangeField} />
            <FileDataHeader />
            {!!countries.length && <InvoiceProfileSection />}
            {!!countries.length && (
              <h4 className="__bold mb-2">
                {t(`${itemFileDataLanguageKey}COMMON_DATA_TITLE`)}
              </h4>
            )}
            {editMode ? (
              <React.Fragment>
                {!!countries.length && (
                  <div className="df-row-start border-form mb-4">
                    <div className="wdthp-25 pl-2 mt-2 pr-4">
                      <ProfileField {...fileDataFieldProps.companyName} />
                      <ProfileField {...fileDataFieldProps.companyNumber} />
                      <ProfileField
                        {...fileDataFieldProps.companyAddressMain}
                        addClass="mt-4 mb-1"
                      />
                      <ProfileField {...fileDataFieldProps.companyAddressExtra} />
                      <div className="df-row-center">
                        <ProfileField
                          {...fileDataFieldProps.companyAddressPostalCode}
                          addClass="mr-2 mb-1"
                        />
                        <ProfileField
                          {...fileDataFieldProps.companyAddressCity}
                          addClass="fg2 mb-1"
                        />
                      </div>
                      <MiniSelect
                        list={countries}
                        selectedValue={values.companyAddressCountry ?? "752"}
                        itemAsObject
                        addDropdownClass="wdth-170"
                        addClass="mb-4"
                        onChange={(value) =>
                          setFieldValue("companyAddressCountry", value)
                        }
                      />
                      <ProfileField {...fileDataFieldProps.initiatingContactFullName} />
                      <ProfileField {...fileDataFieldProps.initiatingContactEmail} />
                      <ProfileField
                        {...fileDataFieldProps.initiatingContactMobilePhone}
                      />
                    </div>
                    <div className="wdthp-25 mt-2 pr-8">
                      <ProfileField {...fileDataFieldProps.deliveryAddressMain} />
                      <ProfileField {...fileDataFieldProps.deliveryAddressExtra} />
                      <div className="df-row-center">
                        <ProfileField
                          {...fileDataFieldProps.deliveryAddressPostalCode}
                          addClass="mr-2 mb-1"
                        />
                        <ProfileField
                          {...fileDataFieldProps.deliveryAddressCity}
                          addClass="fg2 mb-1"
                        />
                      </div>
                      <MiniSelect
                        list={countries}
                        selectedValue={values.deliveryAddressCountry ?? "752"}
                        itemAsObject
                        addDropdownClass="wdth-170"
                        addClass="mb-4"
                        onChange={(value) =>
                          setFieldValue("deliveryAddressCountry", value)
                        }
                      />
                      <ProfileField {...fileDataFieldProps.deliveryContactFullName} />
                      <ProfileField {...fileDataFieldProps.deliveryContactEmail} />
                      <ProfileField {...fileDataFieldProps.deliveryContactMobilePhone} />
                    </div>
                    <div className="wdthp-25 mt-2 pr-4">
                      <CompanyAutocompleteField
                        {...fileDataFieldProps.counterpartyCompanyName}
                        target="name"
                        searchOrganizations={handleSearchOrganizations}
                        onSelectOrganization={handleCounterpartyOrganizationPicked}
                      />
                      <CompanyAutocompleteField
                        {...fileDataFieldProps.counterpartyCompanyNumber}
                        target="companyNumber"
                        searchOrganizations={handleSearchOrganizations}
                        onSelectOrganization={handleCounterpartyOrganizationPicked}
                      />
                      <ProfileField
                        {...fileDataFieldProps.counterpartyCompanyAddressMain}
                        addClass="mt-4 mb-1"
                      />
                      <ProfileField
                        {...fileDataFieldProps.counterpartyCompanyAddressExtra}
                      />
                      <div className="df-row-center">
                        <ProfileField
                          {...fileDataFieldProps.counterpartyCompanyAddressPostalCode}
                          addClass="mr-2 mb-1"
                        />
                        <ProfileField
                          {...fileDataFieldProps.counterpartyCompanyAddressCity}
                          addClass="fg2 mb-1"
                        />
                      </div>
                      <MiniSelect
                        list={countries}
                        selectedValue={values.counterpartyCompanyAddressCountry ?? "752"}
                        itemAsObject
                        addDropdownClass="wdth-170"
                        addClass="mb-4"
                        onChange={(value) =>
                          setFieldValue("counterpartyCompanyAddressCountry", value)
                        }
                      />
                    </div>
                    <div className="wdthp-25 mt-2 pr-2">
                      <CounterpartyContactAutocompleteField
                        {...fileDataFieldProps.counterpartyContactFullName}
                        members={counterpartyOrgMembers}
                      />
                      <ProfileField {...fileDataFieldProps.counterpartyContactEmail} />
                      <ProfileField
                        {...fileDataFieldProps.counterpartyContactMobilePhone}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            ) : (
              <SignContractFileDataView
                data={contract.HsContractFileDatum}
                countries={countries}
                addClass="mb-4"
              />
            )}
            {!editMode && (
              <TitleIcon
                title={t(`${itemFileDataLanguageKey}DETAILS_TITLE`)}
                icon={<IconMailLetter width="19" height="12" addClass="mr-2" />}
                uppercase={false}
                medium
              />
            )}
            {!!countries.length && (
              <RichEditor
                key={`${contract?.id}__file_data_details`}
                initialValue={values?.details || ""}
                placeholder={`${itemFileDataLanguageKey}DETAILS_PLACEHOLDER`}
                mode={editMode ? "edit" : "view"}
                height={[null, 500]}
                templatesType={templatesType.SIGN_ORDER}
                showTemplateButtons
                showExtendedToolbarButtons
                onChange={(value) => setFieldValue("details", value)}
              />
            )}
            {isCompanyProfilesModal && (
              <OurProfilesModal
                onClose={() => setCompanyProfilesModal(false)}
                isOwner={isOwner}
                onChangeProfile={clickChangeProfile}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
});

export default SignContractFileData;
